import { useMemo } from "react";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  QrcodeOutlined,
  RollbackOutlined,
  StopOutlined,
  ToolOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Button, Flex, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import SText from "@/styles/SText";
import { CommonStateEnum } from "@/types/common";
import {
  Equipment,
  EquipmentEnumItem,
  EquipmentRecordTypeEnum,
  EquipmentStatusEnum,
} from "@/types/equipment";

// 设备台账列 Hook 所需的枚举数据与行操作回调。
interface UseEquipmentColumnProps {
  statusItems: EquipmentEnumItem[];
  onDetail: (id: string) => void;
  onEdit: (equipment: Equipment) => void;
  onToggleEnabled: (equipment: Equipment) => void;
  onQrCode: (id: string) => void;
  onAddRecord: (
    id: string,
    presetRecordType?: EquipmentRecordTypeEnum,
  ) => void;
  onViewRecords: (equipment: Equipment) => void;
  onEditPlaceLocation: (equipment: Equipment) => void;
  actionLoadingId?: string;
  actionsDisabled: boolean;
  canEdit: boolean;
}

// 普通字段统一以短横线表示空值，并交由 SText 处理文本溢出。
const renderText = (value?: string | null) => (
  <SText text={value || "-"} />
);

// 生成设备台账列，集中处理状态文案、固定列与行级操作。
const useEquipmentColumn = ({
  statusItems,
  onDetail,
  onEdit,
  onToggleEnabled,
  onQrCode,
  onAddRecord,
  onViewRecords,
  onEditPlaceLocation,
  actionLoadingId,
  actionsDisabled,
  canEdit,
}: UseEquipmentColumnProps): ColumnsType<Equipment> => {
  const { t } = useTranslation();

  return useMemo<ColumnsType<Equipment>>(() => [
    {
      title: t("equipment.equipmentNumber"),
      dataIndex: "number",
      width: 150,
      render: (number: string | null, equipment) => number ? (
        <Tooltip title={number}>
          <Typography.Link
            ellipsis
            style={{ display: "block", width: "100%" }}
            onClick={() => onDetail(equipment.id)}
          >
            {number}
          </Typography.Link>
        </Tooltip>
      ) : renderText(number),
    },
    {
      title: t("equipment.equipmentName"),
      dataIndex: "name",
      width: 180,
      render: renderText,
    },
    {
      title: t("equipment.brand"),
      dataIndex: "brand",
      width: 130,
      render: renderText,
    },
    {
      title: t("equipment.model"),
      dataIndex: "model",
      width: 140,
      render: renderText,
    },
    {
      title: t("equipment.serialNo"),
      dataIndex: "serialNo",
      width: 160,
      render: renderText,
    },
    // 状态文案来自接口枚举，未知值回退为原始数值。
    {
      title: t("equipment.equipmentStatus"),
      dataIndex: "status",
      width: 120,
      ellipsis: true,
      render: (status: number) => (
        <SText
          text={statusItems.find(({ v }) => v === status)?.desc ??
            String(status)}
          tagMode
          style={{ textAlign: "left" }}
          tagStyle={{ marginInlineEnd: 0 }}
        />
      ),
    },
    {
      title: t("equipment.keeperOrg"),
      dataIndex: ["keeperOrg", "name"],
      width: 160,
      render: renderText,
    },
    {
      title: t("equipment.location"),
      dataIndex: "location",
      width: 150,
      render: renderText,
    },
    // 放置地点在展示值旁保留独立的快捷编辑入口。
    {
      title: t("equipment.placeLocation"),
      dataIndex: "placeLocation",
      width: 170,
      render: (placeLocation: string | null, equipment) => (
        <Flex align="center" gap={2} style={{ minWidth: 0 }}>
          <SText
            text={placeLocation || "-"}
            style={{
              display: "inline-block",
              width: "auto",
              maxWidth: "calc(100% - 24px)",
            }}
          />
          {canEdit && (
            <Tooltip title={t("equipment.editPlaceLocation")}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                aria-label={t("equipment.editPlaceLocation")}
                disabled={actionsDisabled}
                style={{ flex: "0 0 auto", width: 22, height: 22, padding: 0 }}
                onClick={() => onEditPlaceLocation(equipment)}
              />
            </Tooltip>
          )}
        </Flex>
      ),
    },
    // 二维码与操作列固定在右侧，横向滚动时仍可操作。
    {
      title: t("equipment.qrCode"),
      key: "qrCode",
      fixed: "right",
      width: 72,
      align: "center",
      render: (_, equipment) => (
        <Tooltip title={t("equipment.qrCode")}>
          <Button
            type="text"
            size="small"
            icon={<QrcodeOutlined />}
            aria-label={t("equipment.qrCode")}
            disabled={actionsDisabled}
            onClick={() => onQrCode(equipment.id)}
          />
        </Tooltip>
      ),
    },
    {
      title: t("equipment.actions"),
      key: "action",
      fixed: "right",
      width: 180,
      render: (_, equipment) => {
        const enabled = equipment.state === CommonStateEnum.ACTIVE;
        const toggleEnabledText = enabled
          ? t("equipment.disableEquipment")
          : t("equipment.enableEquipment");

        return (
          <Flex gap={6}>
            {canEdit && (
              <>
                <Tooltip title={t("common.edit")}>
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    aria-label={t("common.edit")}
                    disabled={actionsDisabled}
                    onClick={() => onEdit(equipment)}
                  />
                </Tooltip>
                <Tooltip title={toggleEnabledText}>
                  <Button
                    type="link"
                    danger={enabled}
                    icon={enabled ? <StopOutlined /> : <CheckCircleOutlined />}
                    aria-label={toggleEnabledText}
                    loading={actionLoadingId === equipment.id}
                    disabled={actionsDisabled}
                    onClick={() => onToggleEnabled(equipment)}
                  />
                </Tooltip>
                <Tooltip title={t("equipment.maintenanceRecord")}>
                  <Button
                    type="link"
                    icon={<ToolOutlined />}
                    aria-label={t("equipment.maintenanceRecord")}
                    disabled={actionsDisabled}
                    onClick={() => onAddRecord(equipment.id)}
                  />
                </Tooltip>
                {equipment.status === EquipmentStatusEnum.IN_STOCK && (
                  <Tooltip title={t("equipment.borrowEquipment")}>
                    <Button
                      type="link"
                      icon={<UserAddOutlined />}
                      aria-label={t("equipment.borrowEquipment")}
                      disabled={actionsDisabled}
                      onClick={() => onAddRecord(
                        equipment.id,
                        EquipmentRecordTypeEnum.BORROW,
                      )}
                    />
                  </Tooltip>
                )}
                {equipment.status === EquipmentStatusEnum.IN_USE && (
                  <Tooltip title={t("equipment.returnEquipment")}>
                    <Button
                      type="link"
                      icon={<RollbackOutlined />}
                      aria-label={t("equipment.returnEquipment")}
                      disabled={actionsDisabled}
                      onClick={() => onAddRecord(
                        equipment.id,
                        EquipmentRecordTypeEnum.RETURN,
                      )}
                    />
                  </Tooltip>
                )}
                {equipment.status !== EquipmentStatusEnum.SCRAPPED && (
                  <Tooltip title={t("equipment.scrapEquipment")}>
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("equipment.scrapEquipment")}
                      disabled={actionsDisabled}
                      onClick={() => onAddRecord(
                        equipment.id,
                        EquipmentRecordTypeEnum.SCRAP,
                      )}
                    />
                  </Tooltip>
                )}
              </>
            )}
            <Tooltip title={t("equipment.recordFlow")}>
              <Button
                type="link"
                icon={<HistoryOutlined />}
                aria-label={t("equipment.recordFlow")}
                onClick={() => onViewRecords(equipment)}
              />
            </Tooltip>
          </Flex>
        );
      },
    },
  ], [
    actionLoadingId,
    actionsDisabled,
    canEdit,
    onAddRecord,
    onDetail,
    onEdit,
    onEditPlaceLocation,
    onQrCode,
    onToggleEnabled,
    onViewRecords,
    statusItems,
    t,
  ]);
};

export default useEquipmentColumn;
